import { NgModule } from '@angular/core';
import { Routes, RouterModule } from '@angular/router';
import { CommonModule } from "@angular/common";
import { CartGuardService } from './core/services/cart-guard.service';
import { CouponLinkGuard } from './core/services/coupon-link.guard';
import { DataGuardService } from './core/services/data-guard.service';
import { RouteNotFoundGuard } from './core/services/route-not-found.guard';
import { DESIGN_SYSTEM_PATH, designSystemPageEnabled } from './shared/design-system-availability';

/**
 * Catálogo do design system. Precisa vir ANTES de `:restaurant` (que casa com qualquer
 * segmento) e só é registrado em ambiente de teste local — fora dele a rota não existe,
 * então `/design-system` cai no fluxo normal de restaurante.
 */
const designSystemRoutes: Routes = designSystemPageEnabled() ? [
  {
    path: DESIGN_SYSTEM_PATH,
    loadChildren: () => import('./pages/design-system/design-system.module').then( m => m.DesignSystemPageModule)
  },
] : [];

// Exportado para o spec: o `**` precisa ser o último filho de `:restaurant`, e isso
// só é verificável com acesso ao array. Ver app-routing.module.spec.ts.
export const routes: Routes = [

  ...designSystemRoutes,

  {
    path: ':restaurant',
    children: [
      {
        path: '',
        loadChildren: () => import('./pages/tabs/tabs.module').then( m => m.TabsPageModule)
      },
      {
        path: 'hours',
        loadChildren: () => import('./pages/header/hours/hours.module').then( m => m.HoursPageModule)
      },
      {
        path: 'info',
        loadChildren: () => import('./pages/header/info/info.module').then( m => m.InfoPageModule)
      },
      {
        path: 'info-tabs',
        loadChildren: () => import('./pages/header/info-tabs/info-tabs.module').then( m => m.InfoTabsPageModule)
      },
      {
        // Antes vivia só no forChild do HeaderPageModule e resolvia por efeito
        // colateral do import estático das abas no tabs.module (Caso 14); com
        // as abas de fato lazy, precisa de entrada própria como hours/info.
        path: 'payments',
        loadChildren: () => import('./pages/header/payments/payments.module').then( m => m.PaymentsPageModule)
      },
      {
        path: 'delivery',
        canActivate: [CartGuardService],
        loadChildren: () => import('./pages/delivery/delivery.module').then( m => m.DeliveryPageModule)
      },
      {
        path: 'shopping-cart',
        loadChildren: () => import('./pages/shopping-cart/shopping-cart.module').then( m => m.ShoppingCartPageModule)
      },
      {
        path: 'reset-password',
        loadChildren: () => import('./pages/reset-password/reset-password.module').then( m => m.ResetPasswordPageModule)
      },
      {
        path: 'login',
        loadChildren: () => import('./pages/login/login.module').then( m => m.LoginPageModule)
      },
      {
        path: 'register',
        loadChildren: () => import('./pages/register/register.module').then( m => m.RegisterPageModule)
      },
      {
        path: 'forgot-password',
        loadChildren: () => import('./pages/forgot-password/forgot-password.module').then( m => m.ForgotPasswordPageModule)
      },
      {
        path: 'payment',
        canActivate: [CartGuardService],
        loadChildren: () => import('./pages/payment/payment.module').then( m => m.PaymentPageModule)
      },  
      {
        path: 'pizza',
        canActivate: [DataGuardService],
        loadChildren: () => import('./pages/menu/components/pizza/pizza.module').then( m => m.PizzaPageModule)
      },
      {
        path: 'pizza-nep',
        canActivate: [DataGuardService],
        loadChildren: () => import('./pages/menu/components/pizza-nep/pizza-nep.module').then( m => m.PizzaPageModule)
      },
      {
        path: 'pizza-intermediary',
        canActivate: [DataGuardService],
        loadChildren: () => import('./pages/menu/components/pizza-intermediary/pizza-intermediary.module').then( m => m.PizzaPageModule)
      },
      {
        path: 'additional-toppings-intermediary',
        canActivate: [DataGuardService],
        loadChildren: () => import('./pages/menu/components/pizza-intermediary/additional-toppings-intermediary/additional-toppings-intermediary.module').then( m => m.AdditionalToppingsIntermediaryPageModule)
      },
      {
        path: 'complements-intermediary',
        canActivate: [DataGuardService],
        loadChildren: () => import('./pages/menu/components/pizza-intermediary/complements-intermediary/complements-intermediary.module').then( m => m.ComplementsIntermediaryPageModule)
      },
      {
        path: 'order-summary',
        canActivate: [DataGuardService],
        loadChildren: () => import('./shared/components/order-summary/order-summary.module').then( m => m.OrderSummaryPageModule)
      },
      {
        path: 'extra',
        canActivate: [DataGuardService],
        loadChildren: () => import('./pages/menu/components/extra/extra.module').then( m => m.ExtraPageModule)
      },
      {
        path: 'track',
        canActivate: [DataGuardService],
        loadChildren: () => import('./pages/track-order/track-order.module').then(m => m.TrackOrderPageModule)
      },
      {
        path: 'rate-order',
        canActivate: [DataGuardService],
        loadChildren: () => import('./pages/rate-order/rate-order.module').then(m =>m.RateOrderPageModule)
      },
      {
        path: 'reviews',
        loadChildren: () => import('./pages/reviews/reviews.module').then( m => m.ReviewsPageModule)
      },
      {
        path: 'prizes',
        loadChildren: () => import('./pages/prize/prize.module').then( m => m.PrizePageModule)
      },
      {
        path: 'cashback',
        loadChildren: () => import('./pages/cashback/cashback.module').then( m => m.CashbackPageModule)
      },
      {
        path: 'credit-cards',
        children: [
          {
            path: '',
            loadChildren: () => import('./pages/credit-cards/credit-cards.module').then( m => m.CreditCardsPageModule),
          },
          {
            path: 'add-card',
            loadChildren: () => import('./pages/credit-cards/add-card/add-card.module').then( m => m.AddCardPageModule),
          }
        ]
      },
      {
        // Link compartilhável do Cupom v2 (https://<loja>.../c/<código>, tela nova de
        // cupons do painel). Sem tela: o guard guarda o código como pendente, devolve o
        // consumidor ao cardápio (que anuncia a chegada) e a aplicação acontece na tela
        // de pagamento. Precisa vir ANTES do catch-all, que engoliria o caminho.
        path: 'c/:couponCode',
        canActivate: [CouponLinkGuard],
        children: []
      },
      /**
       * Catch-all da loja. PRECISA ser a ÚLTIMA rota desta lista: o router casa em
       * ordem, então qualquer rota declarada abaixo daqui nunca seria alcançada, sem
       * erro de build e sem teste vermelho. O invariante está congelado em
       * `app-routing.module.spec.ts` — se você mover ou acrescentar rota depois daqui,
       * o teste falha e explica o porquê.
       *
       * Sem ela, um sufixo inexistente no link da loja (`/<loja>/me`, `/<loja>/1000`)
       * não casa nenhum filho e o Angular lança `Cannot match any routes`, que sobe
       * ao ErrorHandler e deixa a tela quebrada — 132 a 181 devices/dia.
       *
       * É um guard, e não um `redirectTo: ''`, porque o redirect puro consertaria a
       * tela e apagaria a medição: sem NavigationError não há `ClientAppError`, a série
       * cai a zero e "consertado" vira indistinguível de "parou de ser observado". O
       * RouteNotFoundGuard emite antes de redirecionar e devolve o mesmo destino.
       */
      {
        path: '**',
        canActivate: [RouteNotFoundGuard],
        children: []
      },
    ]
  },
];

@NgModule({
  imports: [CommonModule, RouterModule.forRoot(routes, {
    initialNavigation: 'enabledNonBlocking'
})],
  exports: [RouterModule]
})

export class AppRoutingModule {}